import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import request from '../helpers/request';
import { buildProviderCredentialPayload } from '../helpers/modelProviders';

const API_BASE = '/api/v1/agents/admin/models';

/**
 * Custom hook for managing model configurations
 * Admin-only functionality
 * @returns {object} Models state and operations
 */
export const useModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Fetch all models
  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request.get(`${API_BASE}/`);
      setModels(response.data);
    } catch (err) {
      console.error('Failed to fetch models', err);
      const message = err.response?.data?.error || 'Failed to load models.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch available providers
  const fetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const response = await request.get('/api/v1/agents/providers/');
      // Handle both array response and object wrapper (e.g., { providers: [...] })
      const data = response.data;
      const providersList = Array.isArray(data) ? data : (data?.providers || data?.results || []);
      setProviders(providersList);
    } catch (err) {
      console.error('Failed to fetch providers', err);
      const message = err.response?.data?.error || 'Failed to load providers.';
      toast.error(message);
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Create a new model
  const createModel = useCallback(async (data) => {
    try {
      const response = await request.post(`${API_BASE}/`, data);
      toast.success(`Model "${data.display_name || data.name}" created successfully!`);
      await fetchModels();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error creating model:', err);
      const message = err.response?.data?.error || 'Failed to create model.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels]);

  // Update an existing model
  const updateModel = useCallback(async (id, data) => {
    try {
      const response = await request.patch(`${API_BASE}/${id}/`, data);
      toast.success('Model updated successfully!');
      await fetchModels();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error updating model:', err);
      const message = err.response?.data?.error || 'Failed to update model.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels]);

  // Delete a model
  const deleteModel = useCallback(async (id) => {
    try {
      await request.delete(`${API_BASE}/${id}/`);
      toast.warn('Model deleted!');
      await fetchModels();
      return { success: true };
    } catch (err) {
      console.error('Error deleting model:', err);
      const message = err.response?.data?.error || 'Failed to delete model.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels]);

  // Set a model as the default
  const setDefaultModel = useCallback(async (id) => {
    try {
      // First, unset any existing default
      const currentDefault = models.find(m => m.is_default);
      if (currentDefault && currentDefault.id !== id) {
        await request.patch(`${API_BASE}/${currentDefault.id}/`, { is_default: false });
      }
      // Then set the new default
      await request.patch(`${API_BASE}/${id}/`, { is_default: true });
      toast.success('Default model updated!');
      await fetchModels();
      return { success: true };
    } catch (err) {
      console.error('Error setting default model:', err);
      const message = err.response?.data?.error || 'Failed to set default model.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels, models]);

  // Discover models from a provider
  const discoverModels = useCallback(async (credentials) => {
    try {
      const response = await request.post(`${API_BASE}/discover/`, credentials);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error discovering models:', err);
      const message = err.response?.data?.error || 'Failed to discover models.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // Bulk create models
  const bulkCreateModels = useCallback(async (credentials, selectedModels) => {
    try {
      const provider = credentials?.provider;
      const payload = {
        provider,
        ...buildProviderCredentialPayload({
          provider,
          values: credentials,
          operation: 'bulk',
        }),
        models: selectedModels,
      };
      const response = await request.post(`${API_BASE}/bulk/`, payload);
      toast.success(`Successfully imported ${response.data.count} model(s)!`);
      await fetchModels();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error bulk creating models:', err);
      const errorData = err.response?.data;
      if (errorData?.existing) {
        const message = `Models already exist: ${errorData.existing.join(', ')}`;
        toast.error(message);
        return { success: false, error: message, existing: errorData.existing };
      }
      const message = errorData?.error || 'Failed to import models.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels]);

  // Toggle model active status
  const toggleModelActive = useCallback(async (id, isActive) => {
    try {
      await request.patch(`${API_BASE}/${id}/`, { is_active: isActive });
      toast.success(`Model ${isActive ? 'activated' : 'deactivated'}!`);
      await fetchModels();
      return { success: true };
    } catch (err) {
      console.error('Error toggling model status:', err);
      const message = err.response?.data?.error || 'Failed to update model status.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchModels]);

  // Initial fetch on mount
  useEffect(() => {
    fetchModels();
    fetchProviders();
  }, [fetchModels, fetchProviders]);

  return {
    models,
    loading,
    error,
    providers,
    providersLoading,
    fetchModels,
    fetchProviders,
    createModel,
    updateModel,
    deleteModel,
    setDefaultModel,
    discoverModels,
    bulkCreateModels,
    toggleModelActive,
  };
};

export default useModels;
